import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router'

import { SesionProvider, useSesion } from './api/sesion'
import { BotonDeAyuda } from './componentes/Ayuda'
import { ErrorDePantalla } from './componentes/ErrorDePantalla'
import { Pantalla } from './componentes/Pantalla'
import { Cargando } from './componentes/ui'
import { Marco } from './marco/Marco'
import { Creditos } from './paginas/Creditos'
import { Diseno } from './paginas/Diseno'
import { Dominio } from './paginas/Dominio'
import { Idiomas } from './paginas/Idiomas'
import { Disponibilidad } from './paginas/Disponibilidad'
import { Visitas } from './paginas/Visitas'
import { Revision } from './paginas/Revision'
import { Salud } from './paginas/Salud'
import { SearchConsole } from './paginas/SearchConsole'
import { Entregas } from './paginas/Entregas'
import { Agenda } from './paginas/Agenda'
import { Reservas } from './paginas/agenda/Reservas'
import { IrAlContenido, PaginaDeContenidos } from './estructura/Contenidos'
import { PaginaDeTerminos } from './estructura/Terminos'
import { ProponerConIA } from './estructura-ia/ProponerConIA'
import { Entrar } from './paginas/Entrar'
import { Espacios } from './paginas/Espacios'
import { Alta } from './paginas/alta/Alta'
import { Progreso } from './paginas/alta/Progreso'
import { MiCuenta } from './paginas/MiCuenta'
import { MisSitios } from './paginas/MisSitios'
import { ModuloGenerico } from './paginas/Modulo'
import { Olvide } from './paginas/Olvide'
import { Registro } from './paginas/Registro'
import { Restablecer } from './paginas/Restablecer'
import { Socios } from './paginas/Socios'
import { esDeAgencia } from './marca/marca'
import { Clientes as ClientesDeAgencia } from './paginas/agencia/Clientes'
import { Planes as PlanesDeAgencia } from './paginas/agencia/Planes'
import { Cobros as CobrosDeAgencia } from './paginas/agencia/Cobros'
import { CrearCuenta } from './paginas/agencia/CrearCuenta'
import { Marca as MarcaDeAgencia } from './paginas/agencia/Marca'
import { RegistroEnAgencia } from './paginas/agencia/RegistroEnAgencia'
import { Pagos } from './paginas/Pagos'
import { AvisoDeSocio } from './socios/AvisoDeSocio'

/** Sin sesión no se ve nada, y al entrar se vuelve a donde ibas. */
const ExigeSesion = () => {
  const { usuario, cargando } = useSesion()
  if (cargando) return <Cargando que="Comprobando tu sesión" />
  if (!usuario) return <Navigate replace state={{ volver: window.location.pathname + window.location.search }} to="/entrar" />
  return (
    <>
      <Outlet />
      <BotonDeAyuda />
    </>
  )
}

/** La última barrera: si lo que falla es el propio marco, que al menos se diga qué hacer. */
const Rutas = () => {
  const { pathname } = useLocation()
  return (
    <ErrorDePantalla clave={pathname} pantallaEntera>
      <Routes>
        <Route element={<Entrar />} path="/entrar" />
        {/* Recuperar y poner la contraseña: sin sesión, como entrar. */}
        <Route element={<Olvide />} path="/olvide" />
        <Route element={<Restablecer />} path="/restablecer" />
        {/* En el panel de una agencia (§36, R6), el registro es el suyo: su plan, su marca, como cliente suyo. */}
        <Route element={esDeAgencia() ? <RegistroEnAgencia /> : <Registro />} path="/registro" />
        <Route element={<CrearCuenta />} path="/crear-cuenta" />
        <Route element={<ExigeSesion />}>
          <Route element={<Espacios />} path="/" />
          <Route element={<Marco />} path="/e/:espacio">
            {/* Cada pantalla, con la banda de la cuenta suspendida y su barrera de errores (componentes/Pantalla.tsx). */}
            <Route element={<Pantalla />}>
              <Route element={<MisSitios />} index />
              <Route element={<Creditos />} path="creditos" />
              <Route element={<Socios />} path="socios" />
              {/* La agencia revendedora (§36): sus clientes y sus planes. */}
              <Route element={<ClientesDeAgencia />} path="agencia/clientes" />
              <Route element={<PlanesDeAgencia />} path="agencia/planes" />
              <Route element={<CobrosDeAgencia />} path="agencia/cobros" />
              <Route element={<MarcaDeAgencia />} path="agencia/marca" />
              <Route element={<Pagos />} path="pagos" />
              <Route element={<MiCuenta />} path="cuenta" />
              {/* «Crear un sitio» abre el asistente, no el editor genérico (docs/alta-de-web-con-plantillas.md §8). */}
              <Route element={<Alta />} path="sitios/nuevo" />
              <Route element={<ModuloGenerico />} path=":modulo" />
              <Route element={<ModuloGenerico />} path=":modulo/:id" />
              <Route element={<Navigate replace to="paginas" />} path="s/:sitio" />
              <Route element={<Progreso />} path="s/:sitio/alta" />
              <Route element={<Diseno />} path="s/:sitio/diseno" />
              <Route element={<Dominio />} path="s/:sitio/dominio" />
              <Route element={<Idiomas />} path="s/:sitio/idiomas" />
              <Route element={<Disponibilidad />} path="s/:sitio/disponibilidad" />
              <Route element={<Visitas />} path="s/:sitio/visitas" />
              <Route element={<Revision />} path="s/:sitio/revision" />
              <Route element={<Salud />} path="s/:sitio/salud" />
              <Route element={<SearchConsole />} path="s/:sitio/google" />
              {/* Envíos y entregas: zonas por lugares oficiales, recojo y Google Maps (paginas/Entregas.tsx). */}
              <Route element={<Entregas />} path="s/:sitio/entregas" />
              {/* La agenda de reservas (§33): activarla con la plantilla del rubro, y sus ajustes. */}
              <Route element={<Agenda />} path="s/:sitio/agenda" />
              <Route element={<Reservas />} path="s/:sitio/reservas" />
              {/* Los contenidos de cada tipo («Propiedades») y los términos de cada taxonomía (plan del CMS dinámico §2.3 y §2.5). */}
              <Route element={<ProponerConIA />} path="s/:sitio/estructura-ia" />
              <Route element={<PaginaDeContenidos />} path="s/:sitio/contenidos/:tipo" />
              <Route element={<PaginaDeContenidos />} path="s/:sitio/contenidos/:tipo/:id" />
              <Route element={<IrAlContenido />} path="s/:sitio/contenido/:id" />
              <Route element={<PaginaDeTerminos />} path="s/:sitio/taxonomias/:taxonomia/terminos" />
              <Route element={<PaginaDeTerminos />} path="s/:sitio/taxonomias/:taxonomia/terminos/:id" />
              <Route element={<ModuloGenerico />} path="s/:sitio/:modulo" />
              <Route element={<ModuloGenerico />} path="s/:sitio/:modulo/:id" />
            </Route>
          </Route>
        </Route>
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </ErrorDePantalla>
  )
}

export const App = () => {
  // Si la dirección trae `?ref=` de un socio, se pregunta antes de guardar la cookie (§30).
  return (
    <SesionProvider>
      <Rutas />
      {!esDeAgencia() && <AvisoDeSocio />}
    </SesionProvider>
  )
}
